/**
 * `docs/help/` as screens.
 *
 * The seven guides are written as markdown in the repository and reviewed like
 * any other document there. Copying them into components would give the app a
 * second copy that drifts the first time somebody edits one, so they are
 * imported instead — `eager` and `?raw`, which makes them strings in the bundle
 * at build time rather than files fetched at runtime. The API is not involved:
 * help is the same for everybody and depends on nothing a session knows.
 *
 * `README.md` is deliberately excluded. It is the index of the directory for
 * somebody reading the repository, and {@link HelpIndexPage} is the index for
 * somebody reading the application — two audiences, and the repository one
 * links to file paths that mean nothing here.
 */

import { documentToPlainText, markdownToDocument } from '@matchbox/shared/markdown';

const SOURCES = import.meta.glob('../../../../docs/help/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export interface HelpPage {
  /** The URL segment, which is the filename without its extension. */
  slug: string;
  /** The document's own `# ` heading, so the list cannot drift from the page. */
  title: string;
  /** The opening paragraph with its syntax stripped, for the index card. */
  summary: string;
  markdown: string;
}

function slugOf(path: string): string {
  return path.split('/').pop()?.replace(/\.md$/, '') ?? path;
}

/**
 * The first `# ` line. Every one of these documents opens with one, and a
 * fallback of the slug is what a new file gets until somebody adds one — a
 * missing title should look wrong on the page, not throw on the way to it.
 */
function titleOf(markdown: string, slug: string): string {
  const heading = markdown.split('\n').find((line) => line.startsWith('# '));
  return heading ? heading.slice(2).trim() : slug;
}

/**
 * The opening paragraph, as prose rather than as source.
 *
 * Through §5.1's parser and §9.1's plain-text derivation rather than a regular
 * expression over asterisks: these documents use bold, code spans, and links in
 * their first sentence, and a card that reads "**Matchbox staff and
 * contractors** sign in with Google" is worse than no card. The pipeline that
 * strips syntax for the search index is the one that already knows how.
 */
function summaryOf(markdown: string): string {
  const paragraph = markdown
    .split('\n')
    .find((line) => !line.startsWith('#') && line.trim().length > 0);
  return paragraph ? documentToPlainText(markdownToDocument(paragraph)).trim() : '';
}

/**
 * §7.2's reading order, not the filesystem's.
 *
 * `docs/help/README.md` puts these in the order somebody meets them — an
 * account, then writing, then publishing — and alphabetical would open on
 * "Acknowledgments", which is the fifth thing that happens to a new hire.
 */
const ORDER: readonly string[] = [
  'getting-started',
  'writing-a-document',
  'review-and-publishing',
  'comments-and-suggestions',
  'acknowledgments',
  'finding-things',
  'who-can-see-what',
];

export const HELP_PAGES: readonly HelpPage[] = Object.entries(SOURCES)
  .map(([path, markdown]) => {
    const slug = slugOf(path);
    return { slug, title: titleOf(markdown, slug), summary: summaryOf(markdown), markdown };
  })
  .filter((page) => page.slug !== 'README')
  .sort((a, b) => {
    // A file nobody has ordered yet sorts to the end rather than to the front,
    // where an unlisted slug's -1 would otherwise put it.
    const left = ORDER.indexOf(a.slug);
    const right = ORDER.indexOf(b.slug);
    return (left < 0 ? ORDER.length : left) - (right < 0 ? ORDER.length : right);
  });

export function helpPage(slug: string): HelpPage | undefined {
  return HELP_PAGES.find((page) => page.slug === slug);
}
